import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { paymentService, clientService, supplierService, invoiceService, checkService } from '../../services/api';
import { Client, Supplier, Invoice, Payment } from '../../types';
import { toast } from 'react-toastify';

interface PaymentFormProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  payment?: Payment | null;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ show, onHide, onSuccess, payment }) => {
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [formData, setFormData] = useState({
    type: 'received',
    date: new Date().toISOString().split('T')[0],
    amount: '',
    method: 'cash',
    reference: '',
    client_id: '',
    supplier_id: '',
    check_id: '',
    check_number: '',
    bank_name: '',
    notes: '',
  });
  const [selectedInvoices, setSelectedInvoices] = useState<{[key: number]: number}>({});
  const [availableChecks, setAvailableChecks] = useState<any[]>([]);

  useEffect(() => {
    if (show) {
      fetchClients();
      fetchSuppliers();
      if (payment) {
        setFormData({
          type: payment.type,
          date: payment.date,
          amount: payment.amount.toString(),
          method: payment.method,
          reference: payment.reference || '',
          client_id: payment.client_id?.toString() || '',
          supplier_id: payment.supplier_id?.toString() || '',
          check_id: payment.check_id?.toString() || '',
          check_number: payment.check_number || '',
          bank_name: payment.bank_name || '',
          notes: payment.notes || '',
        });
        // Pour le mode édition, initialiser avec la facture existante si elle existe
        if (payment.invoice_id) {
          setSelectedInvoices({ [payment.invoice_id]: payment.amount });
        }
      }
    }
  }, [show, payment]);

  useEffect(() => {
    if (formData.client_id || formData.supplier_id) {
      fetchInvoices();
    }
  }, [formData.client_id, formData.supplier_id, formData.type]);

  useEffect(() => {
    if (formData.method === 'check' && formData.type === 'sent') {
      fetchAvailableChecks();
    }
  }, [formData.method, formData.type]);

  const fetchAvailableChecks = async () => {
    try {
      const response = await checkService.getAll({ status: 'unused' });
      const checks = response.data.data?.data || response.data.data || [];
      setAvailableChecks(Array.isArray(checks) ? checks : []);
    } catch (error) {
      console.error('Erreur lors du chargement des chèques');
      setAvailableChecks([]);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await clientService.getAll();
      setClients(response.data.data);
    } catch (error) {
      console.error('Erreur lors du chargement des clients');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await supplierService.getAll();
      setSuppliers(response.data.data);
    } catch (error) {
      console.error('Erreur lors du chargement des fournisseurs');
    }
  };

  const fetchInvoices = async () => {
    try {
      const params: any = {};
      if (formData.type === 'received' && formData.client_id) {
        params.type = 'sale';
        // Charger toutes les factures du client avec un montant restant > 0
        const response = await invoiceService.getAll(params);
        const clientInvoices = response.data.data.filter((invoice: any) => 
          invoice.client_id?.toString() === formData.client_id && 
          invoice.remaining_amount > 0
        );
        setInvoices(clientInvoices);
      } else if (formData.type === 'sent' && formData.supplier_id) {
        params.type = 'purchase';
        // Charger toutes les factures du fournisseur avec un montant restant > 0
        const response = await invoiceService.getAll(params);
        const supplierInvoices = response.data.data.filter((invoice: any) => 
          invoice.supplier_id?.toString() === formData.supplier_id && 
          invoice.remaining_amount > 0
        );
        setInvoices(supplierInvoices);
      } else {
        setInvoices([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des factures');
      setInvoices([]);
    }
  };

  const handleInvoiceSelection = (invoiceId: number, checked: boolean, remainingAmount: number) => {
    setSelectedInvoices(prev => {
      const newSelected = { ...prev };
      if (checked) {
        newSelected[invoiceId] = remainingAmount;
      } else {
        delete newSelected[invoiceId];
      }
      
      // Calculer le montant total
      const totalAmount = Object.values(newSelected).reduce((sum, amount) => sum + amount, 0);
      setFormData(prevData => ({ ...prevData, amount: totalAmount.toString() }));
      
      return newSelected;
    });
  };

  const handleInvoiceAmountChange = (invoiceId: number, amount: number) => {
    setSelectedInvoices(prev => {
      const newSelected = { ...prev, [invoiceId]: amount };
      
      // Calculer le montant total
      const totalAmount = Object.values(newSelected).reduce((sum, amt) => sum + amt, 0);
      setFormData(prevData => ({ ...prevData, amount: totalAmount.toString() }));
      
      return newSelected;
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      
      // Reset related fields when type changes
      if (name === 'type') {
        newData.client_id = '';
        newData.supplier_id = '';
        setSelectedInvoices({});
      }
      
      // Reset invoices when client/supplier changes
      if (name === 'client_id' || name === 'supplier_id') {
        setSelectedInvoices({});
      }
      
      // Auto-fill check details when check is selected
      if (name === 'check_id' && value) {
        const selectedCheck = availableChecks.find(check => check.id.toString() === value);
        if (selectedCheck) {
          newData.check_number = selectedCheck.check_number;
          newData.bank_name = selectedCheck.checkbook.bank_name;
        }
      }
      
      // Clear check fields when method is not check or type is not sent
      if ((name === 'method' && value !== 'check') || (name === 'type' && value !== 'sent')) {
        newData.check_id = '';
        newData.check_number = '';
        newData.bank_name = '';
      }
      
      return newData;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const baseData = {
        type: formData.type,
        date: formData.date,
        method: formData.method,
        reference: formData.reference || null,
        client_id: formData.client_id ? parseInt(formData.client_id) : null,
        supplier_id: formData.supplier_id ? parseInt(formData.supplier_id) : null,
        check_id: formData.check_id ? parseInt(formData.check_id) : null,
        check_number: formData.check_number || null,
        bank_name: formData.bank_name || null,
        notes: formData.notes || null,
      };

      if (payment) {
        // Mode modification - garder l'ancien comportement
        const data = {
          ...baseData,
          amount: parseFloat(formData.amount),
        };
        await paymentService.update(payment.id, data);
        toast.success('Règlement modifié avec succès');
      } else {
        // Mode création - créer un paiement par facture sélectionnée
        if (Object.keys(selectedInvoices).length > 0) {
          // Paiements liés aux factures
          for (const [invoiceId, amount] of Object.entries(selectedInvoices)) {
            const data = {
              ...baseData,
              amount: amount,
              invoice_id: parseInt(invoiceId),
            };
            await paymentService.create(data);
          }
          toast.success(`${Object.keys(selectedInvoices).length} règlement(s) créé(s) avec succès`);
        } else {
          // Paiement sans facture
          const data = {
            ...baseData,
            amount: parseFloat(formData.amount),
          };
          await paymentService.create(data);
          toast.success('Règlement créé avec succès');
        }
      }
      
      onSuccess();
      onHide();
      resetForm();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde', error); 
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'received',
      date: new Date().toISOString().split('T')[0],
      amount: '',
      method: 'cash',
      reference: '',
      client_id: '',
      supplier_id: '',
      check_id: '',
      check_number: '',
      bank_name: '',
      notes: '',
    });
    setSelectedInvoices({});
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{payment ? 'Modifier' : 'Nouveau'} règlement</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Type *</Form.Label>
                <Form.Select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  required
                >
                  <option value="received">Règlement reçu (Client)</option>
                  <option value="sent">Règlement envoyé (Fournisseur)</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Date *</Form.Label>
                <Form.Control
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Montant * {Object.keys(selectedInvoices).length > 0 && <small className="text-muted">(calculé automatiquement)</small>}</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0.01"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  readOnly={Object.keys(selectedInvoices).length > 0}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Méthode *</Form.Label>
                <Form.Select
                  name="method"
                  value={formData.method}
                  onChange={handleInputChange}
                  required
                >
                  <option value="cash">Espèces</option>
                  <option value="check">Chèque</option>
                  <option value="transfer">Virement</option>
                  <option value="card">Carte</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              {formData.type === 'received' ? (
                <Form.Group className="mb-3">
                  <Form.Label>Client</Form.Label>
                  <Form.Select
                    name="client_id"
                    value={formData.client_id}
                    onChange={handleInputChange}
                  >
                    <option value="">Sélectionner un client</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              ) : (
                <Form.Group className="mb-3">
                  <Form.Label>Fournisseur</Form.Label>
                  <Form.Select
                    name="supplier_id"
                    value={formData.supplier_id}
                    onChange={handleInputChange}
                  >
                    <option value="">Sélectionner un fournisseur</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}
            </Col>
            <Col md={6}>
              {invoices.length > 0 && (
                <Form.Group className="mb-3">
                  <Form.Label>Factures à régler</Form.Label>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ced4da', borderRadius: '0.375rem', padding: '0.5rem' }}>
                    {invoices.map(invoice => (
                      <div key={invoice.id} className="d-flex align-items-center mb-2">
                        <Form.Check
                          type="checkbox"
                          id={`invoice-${invoice.id}`}
                          checked={!!selectedInvoices[invoice.id]}
                          onChange={(e) => handleInvoiceSelection(invoice.id, e.target.checked, Number(invoice.remaining_amount) || 0)}
                          className="me-2"
                        />
                        <div className="flex-grow-1">
                          <small><strong>{invoice.invoice_number}</strong></small><br/>
                          <small>Restant: {(Number(invoice.remaining_amount) || 0).toFixed(2)} MAD</small>
                        </div>
                        {selectedInvoices[invoice.id] && (
                          <Form.Control
                            type="number"
                            step="0.01"
                            min="0.01"
                            max={Number(invoice.remaining_amount) || 0}
                            value={selectedInvoices[invoice.id]}
                            onChange={(e) => handleInvoiceAmountChange(invoice.id, parseFloat(e.target.value) || 0)}
                            style={{ width: '100px' }}
                            size="sm"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </Form.Group>
              )}
            </Col>
          </Row>

          {formData.method === 'check' && formData.type === 'sent' && (
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Chèque</Form.Label>
                  <Form.Select
                    name="check_id"
                    value={formData.check_id}
                    onChange={handleInputChange}
                  >
                    <option value="">Sélectionner un chèque</option>
                    {availableChecks.map(check => (
                      <option key={check.id} value={check.id}>
                        {check.check_number} - {check.checkbook.bank_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>N° Chèque</Form.Label>
                  <Form.Control
                    type="text"
                    name="check_number"
                    value={formData.check_number}
                    onChange={handleInputChange}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Banque</Form.Label>
                  <Form.Control
                    type="text"
                    name="bank_name"
                    value={formData.bank_name}
                    onChange={handleInputChange}
                    readOnly
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Référence</Form.Label>
            <Form.Control
              type="text"
              name="reference"
              value={formData.reference}
              onChange={handleInputChange}
              placeholder="Référence virement, notes..."
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? 'Sauvegarde...' : (payment ? 'Modifier' : 'Créer')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default PaymentForm;